import { useState } from 'react';
import { Dialog, DialogContent } from '../ui/dialog';
import { GenerationLog, MODE_LABELS, GENERATOR_LABELS } from '../../../lib/prompt-admin-api';
import { X } from 'lucide-react';

interface GenerationPreviewModalProps {
    log: GenerationLog;
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
}

export function GenerationPreviewModal({ log, isOpen, onOpenChange }: GenerationPreviewModalProps) {
    const [fullscreenImage, setFullscreenImage] = useState<string | null>(null);

    return (
        <>
            <Dialog key={log.id} open={isOpen} onOpenChange={onOpenChange}>
                <DialogContent className="max-w-7xl max-h-[90vh] overflow-y-auto">
                    <div className="space-y-6">
                        {/* Header */}
                        <div>
                            <h2 className="text-2xl font-bold">Szczegóły generacji</h2>
                            <p className="text-sm text-muted-foreground mt-1">
                                {log.profiles?.email || 'Nieznany użytkownik'} • {new Date(log.created_at).toLocaleString('pl-PL')}
                            </p>
                        </div>

                        {/* Images Grid */}
                        <div className="grid grid-cols-2 gap-6">
                            {/* Input Image */}
                            <div className="space-y-2">
                                <h3 className="text-sm font-semibold text-muted-foreground">Obraz wejściowy</h3>
                                {log.input_image_url ? (
                                    <img
                                        src={log.input_image_url}
                                        alt="Input"
                                        className="w-full rounded-lg border border-border cursor-pointer hover:opacity-90 transition-opacity"
                                        onClick={() => setFullscreenImage(log.input_image_url)}
                                    />
                                ) : (
                                    <div className="w-full aspect-video bg-muted rounded-lg flex items-center justify-center text-muted-foreground">
                                        Brak obrazu
                                    </div>
                                )}
                            </div>

                            {/* Output Images */}
                            <div className="space-y-2">
                                <h3 className="text-sm font-semibold text-muted-foreground">
                                    Wynik ({log.output_variants?.length || 0} wariantów)
                                </h3>
                                {log.output_variants && log.output_variants.length > 0 ? (
                                    <div className="grid grid-cols-2 gap-4">
                                        {log.output_variants.map((variant, idx) => (
                                            <div key={idx} className="space-y-1">
                                                <img
                                                    src={variant.url}
                                                    alt={`Output ${idx + 1}`}
                                                    className="w-full rounded-lg border-2 border-primary cursor-pointer hover:opacity-90 transition-opacity"
                                                    onClick={() => setFullscreenImage(variant.url)}
                                                />
                                                <div className="text-xs text-center">
                                                    <p className="font-medium text-muted-foreground">
                                                        {GENERATOR_LABELS[variant.provider] || variant.provider}
                                                    </p>
                                                    {variant.processing_time_ms && (
                                                        <p className="text-muted-foreground/70">
                                                            {(variant.processing_time_ms / 1000).toFixed(2)}s
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="w-full aspect-video bg-muted rounded-lg flex items-center justify-center text-muted-foreground">
                                        Brak wyników
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Metadata */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-muted/50 rounded-lg">
                            <div>
                                <p className="text-xs text-muted-foreground">Tryb</p>
                                <p className="text-sm font-medium">{MODE_LABELS[log.mode] || log.mode}</p>
                            </div>
                            <div>
                                <p className="text-xs text-muted-foreground">Status</p>
                                <p className={`text-sm font-medium ${
                                    log.status === 'completed' || log.status === 'success'
                                        ? 'text-green-500'
                                        : log.status === 'failed' || log.status === 'error'
                                        ? 'text-red-500'
                                        : 'text-yellow-500'
                                }`}>
                                    {log.status}
                                </p>
                            </div>
                            <div>
                                <p className="text-xs text-muted-foreground">Kredyty</p>
                                <p className="text-sm font-medium">{log.credits_used}</p>
                            </div>
                            {log.output_variants && log.output_variants.length > 0 && (
                                <div>
                                    <p className="text-xs text-muted-foreground">Czas przetwarzania</p>
                                    <p className="text-sm font-medium">
                                        {(() => {
                                            const times = log.output_variants
                                                .map(v => v.processing_time_ms)
                                                .filter((t): t is number => t != null);
                                            if (times.length === 0) return 'Brak danych';
                                            const avgTime = times.reduce((a, b) => a + b, 0) / times.length;
                                            return `${(avgTime / 1000).toFixed(2)}s śr.`;
                                        })()}
                                    </p>
                                </div>
                            )}
                            {log.style && (
                                <div>
                                    <p className="text-xs text-muted-foreground">Styl</p>
                                    <p className="text-sm font-medium">{log.style}</p>
                                </div>
                            )}
                            {log.target_group && (
                                <div>
                                    <p className="text-xs text-muted-foreground">Grupa docelowa</p>
                                    <p className="text-sm font-medium">{log.target_group}</p>
                                </div>
                            )}
                            {log.room_type && (
                                <div>
                                    <p className="text-xs text-muted-foreground">Typ pomieszczenia</p>
                                    <p className="text-sm font-medium">{log.room_type}</p>
                                </div>
                            )}
                        </div>

                        {/* Prompt Used - show if any variant has prompt */}
                        {log.output_variants?.some(v => v.prompt_used) && (
                            <div className="space-y-2">
                                <h3 className="text-sm font-semibold text-muted-foreground">Użyty prompt</h3>
                                {/* If all prompts are the same, show one */}
                                {log.output_variants.every(v => v.prompt_used === log.output_variants[0].prompt_used) ? (
                                    <pre className="bg-muted p-4 rounded-lg text-xs overflow-x-auto whitespace-pre-wrap">
                                        {log.output_variants[0].prompt_used}
                                    </pre>
                                ) : (
                                    /* If different, show per variant */
                                    <div className="space-y-3">
                                        {log.output_variants.map((variant, idx) => (
                                            variant.prompt_used && (
                                                <div key={idx}>
                                                    <p className="text-xs text-muted-foreground mb-1">
                                                        {GENERATOR_LABELS[variant.provider] || variant.provider}:
                                                    </p>
                                                    <pre className="bg-muted p-3 rounded-lg text-xs overflow-x-auto whitespace-pre-wrap">
                                                        {variant.prompt_used}
                                                    </pre>
                                                </div>
                                            )
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Error Message */}
                        {log.error_message && (
                            <div className="space-y-2">
                                <h3 className="text-sm font-semibold text-red-500">Błąd</h3>
                                <p className="bg-red-500/10 p-4 rounded-lg text-sm text-red-500">
                                    {log.error_message}
                                </p>
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            {/* Fullscreen Image Modal */}
            {fullscreenImage && (
                <div
                    className="fixed inset-0 z-[1300] bg-black/90 flex items-center justify-center cursor-pointer"
                    onClick={() => setFullscreenImage(null)}
                >
                    <button
                        className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
                        onClick={() => setFullscreenImage(null)}
                    >
                        <X className="w-8 h-8" />
                    </button>
                    <img
                        src={fullscreenImage}
                        alt="Fullscreen"
                        className="max-w-[95vw] max-h-[95vh] object-contain"
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
            )}
        </>
    );
}
